import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function GigForm({ gig, initialDate, onSave, onCancel }) {
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [venueInput, setVenueInput] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [clientInput, setClientInput] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    venue: '',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    daily_rate: '',
    rate_type: 'daily',
    paid: false,
    is_substitution: false,
    gig_type: 'mine',
    substitute_name: '',
    substitute_amount: '',
    subbing_for: '',
    notes: '',
    is_canceled: false,
    canceled_paid: false
  });

  useEffect(() => {
    fetchClients();
    fetchLocations();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  useEffect(() => {
    if (gig && gig.id) {
      // Handle daily_rate - convert to string, handling 0 and null/undefined
      const dailyRateValue = (gig.daily_rate !== undefined && gig.daily_rate !== null) 
        ? String(gig.daily_rate) 
        : '';
      
      // Determine gig_type: use gig_type if available, otherwise derive from is_substitution
      const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
      
      const clientId = (gig.client_id !== undefined && gig.client_id !== null) ? String(gig.client_id) : '';
      const client = clients.find(c => String(c.id) === clientId);
      const clientName = client ? (client.company ? client.company : client.name) : (gig.client_name || '');
      
      setFormData({
        title: gig.title || '',
        client_id: clientId,
        venue: gig.venue || '',
        start_date: gig.start_date ? moment(gig.start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        end_date: gig.end_date ? moment(gig.end_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        daily_rate: dailyRateValue,
        rate_type: gig.rate_type || 'daily',
        paid: gig.paid === 1 || gig.paid === true,
        is_substitution: gig.is_substitution === 1 || gig.is_substitution === true,
        gig_type: gigType,
        substitute_name: '', // Don't populate for edits - info should be in additional charges
        substitute_amount: '', // Don't populate for edits - info should be in additional charges
        subbing_for: gig.subbing_for || '',
        notes: gig.notes || '',
        is_canceled: gig.is_canceled === 1 || gig.is_canceled === true,
        canceled_paid: gig.canceled_paid === 1 || gig.canceled_paid === true
      });
      setVenueInput(gig.venue || '');
      setClientInput(clientName);
      setSelectedClientId(clientId);
    } else {
      // Reset form when not editing - use initialDate if provided, otherwise use today
      const defaultDate = initialDate || moment().format('YYYY-MM-DD');
      setFormData({
        title: '',
        client_id: '',
        venue: '',
        start_date: defaultDate,
        end_date: defaultDate,
        daily_rate: '',
        rate_type: 'daily',
        paid: false,
        is_substitution: false,
        gig_type: 'mine',
        substitute_name: '',
        substitute_amount: '',
        subbing_for: '',
        notes: '',
        is_canceled: false,
        canceled_paid: false
      });
      setVenueInput('');
      setClientInput('');
      setSelectedClientId('');
    }
  }, [gig, clients, initialDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVenueChange = (e) => {
    const value = e.target.value;
    setVenueInput(value);
    setFormData(prev => ({
      ...prev,
      venue: value
    }));
    setShowLocationSuggestions(value.length > 0);
  };

  const handleLocationSelect = (locationName) => {
    setVenueInput(locationName);
    setFormData(prev => ({
      ...prev,
      venue: locationName
    }));
    setShowLocationSuggestions(false);
  };

  const handleClientChange = (e) => {
    const value = e.target.value;
    setClientInput(value);
    setShowClientSuggestions(value.length > 0);
    
    // Try to find matching client
    const matchingClient = clients.find(client => {
      const nameMatch = client.name.toLowerCase() === value.toLowerCase();
      const companyMatch = client.company && client.company.toLowerCase() === value.toLowerCase();
      return nameMatch || companyMatch;
    });
    
    if (matchingClient) {
      setSelectedClientId(String(matchingClient.id));
      setFormData(prev => ({
        ...prev,
        client_id: String(matchingClient.id)
      }));
    } else {
      setSelectedClientId('');
      setFormData(prev => ({
        ...prev,
        client_id: ''
      }));
    }
  };

  const handleClientSelect = (client) => {
    setClientInput(client.company || client.name);
    setSelectedClientId(String(client.id));
    setFormData(prev => ({
      ...prev,
      client_id: String(client.id)
    }));
    setShowClientSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_date || !formData.end_date || !formData.daily_rate) {
      alert('Please fill in all required fields');
      return;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      alert('End date must be after start date');
      return;
    }
    
    // Validate substitute fields for new sub_for_me gigs
    if (!gig && formData.gig_type === 'sub_for_me') {
      if (!formData.substitute_name || !formData.substitute_name.trim()) {
        alert('Please enter the substitute name');
        return;
      }
      if (!formData.substitute_amount || parseFloat(formData.substitute_amount) <= 0) {
        alert('Please enter a valid amount for the substitute');
        return;
      }
    }

    // If client input is provided but no client_id is set, create a new client
    if (clientInput.trim() && !selectedClientId) {
      try {
        const clientName = clientInput.trim();
        // Create client with the input as company name (and use it as name too if not provided)
        const newClientResponse = await axios.post(`${API_BASE}/clients`, {
          name: clientName, // Use input as name
          company: clientName // Use input as company name
        });
        const newClientId = String(newClientResponse.data.id);
        setSelectedClientId(newClientId);
        // Refresh clients list to include the new client
        await fetchClients();
        // Update formData with new client_id before saving
        const updatedFormData = {
          ...formData,
          client_id: newClientId
        };
        onSave(updatedFormData);
      } catch (error) {
        console.error('Error creating client:', error);
        alert('Error creating client. Please try again.');
        return;
      }
    } else {
      onSave(formData);
    }
  };

  const filteredLocations = locations.filter(loc => 
    loc.name.toLowerCase().includes(venueInput.toLowerCase())
  );

  const filteredClients = clients.filter(client => {
    const searchTerm = clientInput.toLowerCase();
    const nameMatch = client.name.toLowerCase().includes(searchTerm);
    const companyMatch = client.company && client.company.toLowerCase().includes(searchTerm);
    return nameMatch || companyMatch;
  });

  return (
    <div className="gig-form" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <form id="gig-form" onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', paddingBottom: '1rem', minHeight: 0 }}>
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="e.g., Website Redesign Project"
          />
        </div>

        <div className="form-group">
          <label htmlFor="client_input">Client / Company</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              id="client_input"
              name="client_input"
              value={clientInput}
              onChange={handleClientChange}
              onFocus={() => setShowClientSuggestions(clientInput.length > 0)}
              onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
              placeholder="Type client or company name (creates new if not found)"
              autoComplete="off"
            />
            {showClientSuggestions && filteredClients.length > 0 && (
              <div className="location-suggestions">
                {filteredClients.map(client => (
                  <div
                    key={client.id}
                    className="location-suggestion-item"
                    onMouseDown={() => handleClientSelect(client)}
                  >
                    {client.company || client.name} {client.company && client.name !== client.company ? `(${client.name})` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="venue">Venue/Location</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              id="venue"
              name="venue"
              value={venueInput}
              onChange={handleVenueChange}
              onFocus={() => setShowLocationSuggestions(venueInput.length > 0)}
              onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
              placeholder="Enter venue or location"
              list="venue-suggestions"
              autoComplete="off"
            />
            {showLocationSuggestions && filteredLocations.length > 0 && (
              <div className="location-suggestions">
                {filteredLocations.map(location => (
                  <div
                    key={location.id}
                    className="location-suggestion-item"
                    onMouseDown={() => handleLocationSelect(location.name)}
                  >
                    {location.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="start_date">Start Date *</label>
          <input
            type="date"
            id="start_date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="end_date">End Date *</label>
          <input
            type="date"
            id="end_date"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="rate_type">Rate Type *</label>
          <select
            id="rate_type"
            name="rate_type"
            value={formData.rate_type}
            onChange={handleChange}
            required
          >
            <option value="daily">Daily Rate</option>
            <option value="flat">Flat Rate</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="daily_rate">
            {formData.rate_type === 'flat' ? 'Flat Rate (€)' : 'Daily Rate (€)'} *
          </label>
          <input
            type="number"
            id="daily_rate"
            name="daily_rate"
            value={formData.daily_rate}
            onChange={handleChange}
            required
            min="0"
            step="0.01"
            placeholder={formData.rate_type === 'flat' ? "2000.00" : "500.00"}
          />
          {formData.rate_type === 'flat' && (
            <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.5rem' }}>
              This is a fixed rate for the entire gig, regardless of duration
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="gig_type">Gig Type *</label>
          <select
            id="gig_type"
            name="gig_type"
            value={formData.gig_type}
            onChange={(e) => {
              const newGigType = e.target.value;
              setFormData(prev => ({
                ...prev,
                gig_type: newGigType,
                is_substitution: newGigType !== 'mine', // Keep backward compatibility
                // Clear substitute fields if switching away from sub_for_me
                substitute_name: newGigType !== 'sub_for_me' ? '' : prev.substitute_name,
                substitute_amount: newGigType !== 'sub_for_me' ? '' : prev.substitute_amount
              }));
            }}
            required
          >
            <option value="mine">My Gig</option>
            <option value="sub_for_other">Substituting for Someone</option>
            <option value="sub_for_me">Someone Substituting for Me</option>
          </select>
          <p className="form-helper-text">
            {formData.gig_type === 'mine' && 'This is your own gig'}
            {formData.gig_type === 'sub_for_other' && 'You are filling in for someone else'}
            {formData.gig_type === 'sub_for_me' && 'Someone else is filling in for you'}
          </p>
        </div>

        {/* Show "subbing for" field when gig_type is sub_for_other */}
        {formData.gig_type === 'sub_for_other' && (
          <div className="form-group">
            <label htmlFor="subbing_for">Subbing For *</label>
            <input
              type="text"
              id="subbing_for"
              name="subbing_for"
              value={formData.subbing_for}
              onChange={handleChange}
              placeholder="Name of the person you are subbing for"
              required={formData.gig_type === 'sub_for_other'}
            />
            <p className="form-helper-text">
              Enter the name of the person whose gig you are covering
            </p>
          </div>
        )}

        {/* Only show substitute fields for NEW gigs (not when editing) */}
        {formData.gig_type === 'sub_for_me' && !gig && (
          <>
            <div className="form-group">
              <label htmlFor="substitute_name">Substitute Name *</label>
              <input
                type="text"
                id="substitute_name"
                name="substitute_name"
                value={formData.substitute_name}
                onChange={handleChange}
                placeholder="Name of the person substituting for you"
                required={formData.gig_type === 'sub_for_me'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="substitute_amount">Amount to Pay Substitute (€) *</label>
              <input
                type="number"
                id="substitute_amount"
                name="substitute_amount"
                value={formData.substitute_amount}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                required={formData.gig_type === 'sub_for_me'}
              />
              <p className="form-helper-text">
                The amount you need to pay the substitute for this gig. This will create an additional charge that can be edited later.
              </p>
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Additional information about the gig..."
          />
        </div>

      </form>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" form="gig-form" className="btn btn-primary">
          {gig ? 'Update' : 'Create'} Gig
        </button>
      </div>
    </div>
  );
}

export default GigForm;

